import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Typography, Spin, Tag, Empty, Table } from "antd";
import { Link } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import UsersImg from "../../assets/images/dashbord-user.png";
import CatgoriesImg from "../../assets/images/dashbord-categories.png";
import Servisecatalogue1Img from "../../assets/images/total-servise-catalogue-1.png";

const { Title } = Typography;

const AdminDashboard = () => {
  const { request } = useRequest();
  const [stats, setStats] = useState({
    usersCount: 0,
    recipesCount: 0,
    customIngredientsAddedCount: 0,
    last10RecipesCount: 0,
    ingredientsCount: 0,
  });
  const [last10Recipes, setLast10Recipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    request({
      url: apiPath.authDashboardStats,
      method: "GET",
      onSuccess: (data) => {
        const cards = data?.dashboardCards || {};
        setStats({
          usersCount: cards?.usersCount ?? data?.usersCount ?? 0,
          recipesCount: cards?.recipesCount ?? data?.recipesCount ?? 0,
          customIngredientsAddedCount:
            cards?.customIngredientsAddedCount ??
            data?.customIngredientsAddedCount ??
            data?.ingredientsCount ??
            0,
          last10RecipesCount:
            cards?.last10RecipesCount ?? data?.last10RecipesCount ?? (data?.last10Recipes || []).length ?? 0,
          ingredientsCount: data?.ingredientsCount ?? 0,
        });
        setLast10Recipes(Array.isArray(data?.last10Recipes) ? data.last10Recipes : []);
        setLoading(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Could not load dashboard stats";
        ShowToast(msg, Severty.ERROR);
        setLoading(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const cards = useMemo(
    () => [
    {
      key: "users",
      title: "# of Users",
      value: stats.usersCount,
      icon: UsersImg,
      to: "/user-manager",
    },
    {
      key: "recipes",
      title: "# of Recipes",
      value: stats.recipesCount,
      icon: Servisecatalogue1Img,
      to: "/dashboard",
    },
    {
      key: "ingredients",
      title: "Custom Ingredients Added",
      value: stats.customIngredientsAddedCount,
      icon: CatgoriesImg,
      to: "/custom-ingredients",
    },
  ],
    [stats]
  );

  const statusColorMap = {
    active: "green",
    inactive: "default",
    featured: "orange",
  };
  const stageLabelMap = {
    draft: "Draft",
    composition_ready: "Composition Ready",
    costed: "Costed",
    margin_ready: "Margin Ready",
    compliance_checked: "Compliance Checked",
  };
  const channelLabelMap = {
    retail_wholesale: "Retail/Wholesale",
    dtc: "D2C",
  };
  const priceModeLabelMap = {
    retail_driven: "Retail Driven",
    margin_driven: "Margin Driven",
  };

  const formatMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "N/A";
    return `GBP ${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "N/A";
    return `${num.toFixed(1)}%`;
  };

  const getMarginColor = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "default";
    if (num < 25) return "red";
    if (num <= 40) return "green";
    return "lime";
  };

  const getLossColor = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "default";
    if (num < 2) return "green";
    if (num <= 6) return "orange";
    return "red";
  };

  const last10TableColumns = [
    {
      title: "Recipe",
      dataIndex: "name",
      key: "name",
      render: (_, item) => item?.name || "Untitled Recipe",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag color={statusColorMap[(value || "").toLowerCase()] || "default"}>
          {(value || "unknown").toLowerCase()}
        </Tag>
      ),
    },
    // {
    //   title: "Stage",
    //   dataIndex: "stage",
    //   key: "stage",
    //   render: (value) => <Tag color="geekblue">{stageLabelMap[value] || "Draft"}</Tag>,
    // },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (value) =>
        value ? <Tag color="purple">{channelLabelMap[value] || String(value)}</Tag> : "-",
    },
    {
      title: "Price Mode",
      dataIndex: "priceMode",
      key: "priceMode",
      render: (value) =>
        value ? <Tag color="gold">{priceModeLabelMap[value] || String(value)}</Tag> : "-",
    },
    {
      title: "COGS / Pack",
      dataIndex: "cogsPerPack",
      key: "cogsPerPack",
      render: (value) => formatMoney(value),
    },
    {
      title: "Selling Price",
      dataIndex: "sellingPricePerPack",
      key: "sellingPricePerPack",
      render: (value) => formatMoney(value),
    },
    {
      title: "Margin",
      dataIndex: "grossMarginPercent",
      key: "grossMarginPercent",
      render: (value, item) => (
        <Tag color={getMarginColor(value)}>{`${item?.marginLabel || "Margin"}: ${formatPercent(value)}`}</Tag>
      ),
    },
    {
      title: "HFSS",
      dataIndex: "isHFSS",
      key: "isHFSS",
      render: (value) => (
        <Tag color={value === true ? "red" : "green"}>
          {value === true ? "Risk" : "OK"}
        </Tag>
      ),
    },
    {
      title: "Process Loss",
      dataIndex: "processLossPercent",
      key: "processLossPercent",
      render: (value) => <Tag color={getLossColor(value)}>{formatPercent(value)}</Tag>,
    },
    {
      title: "Ingredients",
      dataIndex: "ingredientCount",
      key: "ingredientCount",
      render: (value) => (Number.isFinite(Number(value)) ? Number(value) : 0),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      render: (_, item) => item?.createdByName || item?.createdByEmail || "Unknown",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 8 }}>
        Dashboard
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Overview and quick links to admin modules.
      </Typography.Paragraph>

      <Row gutter={[24, 16]} className="rowgap-vbox">
        {cards.map((m) => (
          <Col xs={24} sm={12} md={12} lg={8} key={m.key}>
            <Link to={m.to}>
              <Card
                bordered={false}
                className="criclebox icon-box-text_mainhh"
                style={{ height: "100%", cursor: "pointer" }}
                hoverable
              >
                <div className="number">
                  <div className="icon-box-text">
                    <span>{m.title}</span>
                    <Title level={3}>
                      {loading ? <Spin size="small" /> : m.value}
                    </Title>
                  </div>
                  <div className="icon-box">
                    <img src={m.icon} alt="" />
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Card
        id="last-10-recipes-section"
        bordered={false}
        className="criclebox"
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, color: "#8c8c8c" }}>Last 10 Recipes | {stats.last10RecipesCount}</span>
          </div>
        }
        style={{ marginTop: 24 }}
      >
        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>
            <Spin />
          </div>
        ) : last10Recipes.length === 0 ? (
          <Empty description="No recipes found" />
        ) : (
          <Table
            rowKey={(item) => item?.id || item?._id}
            size="small"
            pagination={false}
            columns={last10TableColumns}
            dataSource={last10Recipes}
            scroll={{ x: 1450 }}
          />
        )}
      </Card>
    </div>
  );
};

export default AdminDashboard;
